// The plan an agent prints in a reply (SUN-1346), moved from today's Code tab
// (code/plan.ts there); and the plan ACP's `plan` update sends, drawn as the
// same checklist (planOfEntries). AGENTS.md has a lead keep him informed
// with a checklist:
//
//     Plan: <name> · <completed>/<total> steps
//     ✔ completed steps
//     ▶ current step
//     pending steps
//
// Markdown joins lines that no blank line parts, so printed as plain lines
// it read as one paragraph, and printed as a block as grey code. Here a
// reply's text is taken apart into what Markdown draws and the plans in it,
// which the page draws as a checklist (PlanBlock.tsx).
//
// A plan is its head line and the lines that follow it, up to a blank line
// or the end of the block of code it was printed in: `✔` says a step is
// done, `▶` that it runs now, and any other line is a step still to come.
// Nothing else is a plan: a line that only looks like the head inside a
// longer block of code stays the code it is.
import type { PlanStepStatus } from './types'

export type StepState = 'done' | 'now' | 'todo'

export interface PlanStep {
  state: StepState
  /** The number the agent gave the step, when it gave every step one. */
  label?: string
  text: string
}

export interface Plan {
  name: string
  /** As its head line says: how many steps are done, of how many. */
  done: number
  total: number
  steps: PlanStep[]
}

export type ReplyPart = { kind: 'text'; text: string } | { kind: 'plan'; plan: Plan }

const HEAD = /^[ \t]*Plan:[ \t]+(.+?)[ \t]+[·•][ \t]+(\d{1,4})[ \t]*\/[ \t]*(\d{1,4})[ \t]+steps?[ \t]*$/
const FENCE = /^[ \t]*(`{3,}|~{3,})/
const DONE = /^(?:[✔✓☑]\uFE0F?|✅)[ \t]*/
const NOW = /^[▶►▸⏵]\uFE0F?[ \t]*/
const NUMBERED = /^(\d{1,3})[.):]?[ \t]+(\S.*)$/

function step(line: string): PlanStep {
  const text = line.trim()
  if (DONE.test(text)) return { state: 'done', text: text.replace(DONE, '') }
  if (NOW.test(text)) return { state: 'now', text: text.replace(NOW, '') }
  return { state: 'todo', text }
}

/** The steps with their numbers apart from their words, when the agent numbered every one. */
function numbered(steps: PlanStep[]): PlanStep[] {
  const found = steps.map((one) => NUMBERED.exec(one.text))
  if (steps.length === 0 || found.some((match) => match === null)) return steps
  return steps.map((one, at) => ({ state: one.state, label: found[at]![1]!, text: found[at]![2]! }))
}

/** Whether `line` ends the block of code that `mark` began: the same mark, at least as long, and nothing else. */
function closes(line: string, mark: string): boolean {
  const held = line.trim()
  return held.length >= mark.length && held === mark[0]!.repeat(held.length)
}

function planOf(head: RegExpExecArray, lines: string[]): Plan {
  return { name: head[1]!.trim(), done: Number(head[2]), total: Number(head[3]), steps: numbered(lines.filter((line) => line.trim() !== '').map(step)) }
}

/**
 * A reply's text as the page draws it: what is Markdown, and the plans in
 * it, in order. A text that holds no plan is one part, itself, unchanged.
 */
export function replyParts(source: string): ReplyPart[] {
  if (!source.includes('Plan:')) return [{ kind: 'text', text: source }]
  const lines = source.split('\n')
  const parts: ReplyPart[] = []
  let text: string[] = []
  const flush = () => {
    if (text.join('\n').trim() !== '') parts.push({ kind: 'text', text: text.join('\n') })
    text = []
  }
  let at = 0
  while (at < lines.length) {
    const line = lines[at]!
    const fence = FENCE.exec(line)
    if (fence) {
      // A block of code: a plan when the plan is all it holds, else the code it is.
      const mark = fence[1]!
      let end = at + 1
      while (end < lines.length && !closes(lines[end]!, mark)) end++
      const inside = lines.slice(at + 1, end)
      const head = inside.length > 0 ? HEAD.exec(inside[0]!) : null
      if (head) {
        flush()
        parts.push({ kind: 'plan', plan: planOf(head, inside.slice(1)) })
      } else {
        for (const held of lines.slice(at, Math.min(end + 1, lines.length))) text.push(held)
      }
      at = end + 1
      continue
    }
    const head = HEAD.exec(line)
    if (head) {
      let end = at + 1
      while (end < lines.length && lines[end]!.trim() !== '' && !FENCE.test(lines[end]!)) end++
      flush()
      parts.push({ kind: 'plan', plan: planOf(head, lines.slice(at + 1, end)) })
      at = end
      continue
    }
    text.push(line)
    at++
  }
  flush()
  return parts.length > 0 ? parts : [{ kind: 'text', text: source }]
}

const STATE_OF: Record<PlanStepStatus, StepState> = { completed: 'done', in_progress: 'now', pending: 'todo' }

/**
 * The plan ACP's `plan` update sends, as the checklist a printed one is: its
 * entries are its steps, in order. It has no name, and as many steps are
 * done as its entries say.
 */
export function planOfEntries(entries: readonly { content: string; status: PlanStepStatus }[]): Plan {
  const steps = entries.map((entry): PlanStep => ({ state: STATE_OF[entry.status] ?? 'todo', text: entry.content }))
  return { name: '', done: steps.filter((one) => one.state === 'done').length, total: steps.length, steps }
}
